import { api } from "@/shared/lib/axios/axios.api"
import { API_PREFIX } from "@/shared/lib/axios/axios.constants"
import type {
  CATReferenceFile,
  CATReferenceList,
  CATReferenceResponse,
  CATReferenceSearchRequest,
  CATReferenceSearchResult,
  CATReferenceTextPage,
  ReferenceTextUnavailableReason,
} from "./reference.types"

/**
 * Every call here is quiet on a 500 (`suppressServerErrorAlert`): the panel lists and searches on
 * its own as the translator moves between segments, and says what failed where it happened.
 *
 * While the server's `CAT_REFERENCES_ENABLED` is off, none of these routes exist and every call is
 * a 404. The panel is drawn only when the workspace reports `features.references`, which the same
 * flag decides, so a 404 here is a deployment between the two and is shown as unavailable.
 */
const quiet = { suppressServerErrorAlert: true } as const

const referencesPath = (jobId: string) =>
  `${API_PREFIX}/jobs/${jobId}/cat-workspace/references`

const referencePath = (jobId: string, referenceId: string) =>
  `${referencesPath(jobId)}/${encodeURIComponent(referenceId)}`

export const listCATReferences = async (jobId: string) => {
  const response = await api.get<CATReferenceList>(referencesPath(jobId), quiet)
  return response.data
}

/** `offset` and `limit` count Unicode scalars. The server reads a `limit` over 100,000 as 100,000. */
export const getCATReferenceText = async ({
  jobId,
  referenceId,
  offset,
  limit,
}: {
  jobId: string
  referenceId: string
  offset: number
  limit: number
}) => {
  const response = await api.get<CATReferenceTextPage>(
    `${referencePath(jobId, referenceId)}/text`,
    { ...quiet, params: { offset, limit } }
  )
  return response.data
}

/** A presigned URL for the original bytes, good for a few minutes: fetch it right away. */
export const getCATReferenceFile = async ({
  jobId,
  referenceId,
}: {
  jobId: string
  referenceId: string
}) => {
  const response = await api.get<CATReferenceFile>(
    `${referencePath(jobId, referenceId)}/file`,
    quiet
  )
  return response.data
}

export const searchCATReferences = async ({
  jobId,
  request,
}: {
  jobId: string
  request: CATReferenceSearchRequest
}) => {
  const response = await api.post<CATReferenceSearchResult>(
    `${referencesPath(jobId)}/search`,
    request,
    quiet
  )
  return response.data
}

/**
 * Adds `file` to the job: `multipart/form-data` with exactly one `file` part, whose name decides
 * the format. The server reads the text before it answers, up to two minutes, and adds the
 * reference whatever it made of the file (`textAvailable`, `textUnavailableReason`); a read that
 * failed for a reason that is not the file's adds nothing and is `503 text_extraction_unavailable`.
 *
 * The FormData skips the request interceptor's decamelizing; the 201 is camelized like any other
 * JSON response.
 */
export const uploadCATReference = async ({
  jobId,
  file,
}: {
  jobId: string
  file: File
}) => {
  const form = new FormData()
  form.append("file", file, file.name)
  // No Content-Type here: axios writes `multipart/form-data` with its boundary from the FormData.
  const response = await api.post<CATReferenceResponse>(
    referencesPath(jobId),
    form,
    quiet
  )
  return response.data.reference
}

/**
 * Copies another job's reference into this one by its `id` there (`source_reference_id`), never
 * by `file_id` alone: a `file_id` names one reference only while its job holds those bytes under
 * one name and format, and is `400 file_id_ambiguous` otherwise. The copy shares the bytes and
 * text, takes the source's name and format, and starts with no imports.
 */
export const copyCATReference = async ({
  jobId,
  sourceReferenceId,
}: {
  jobId: string
  sourceReferenceId: string
}) => {
  const response = await api.post<CATReferenceResponse>(
    referencesPath(jobId),
    { sourceReferenceId },
    quiet
  )
  return response.data.reference
}

/** Removes the job's reference; the stored file stays while another reference shares it. */
export const deleteCATReference = async ({
  jobId,
  referenceId,
}: {
  jobId: string
  referenceId: string
}) => {
  await api.delete(referencePath(jobId, referenceId), quiet)
}

const REFERENCE_BYTES_FETCH_TIMEOUT_MS = 60_000

export class ReferenceDownloadError extends Error {
  constructor(message: string) {
    super(message)
    this.name = "ReferenceDownloadError"
  }
}

/**
 * The bytes behind a `downloadUrl`. Without credentials: the URL is a presigned object-store GET
 * on another origin, the same path the CAT artifact downloads take (`fetchCatXliffArtifact`), so
 * the bucket's CORS already allows it. The object is served as `application/octet-stream` whatever
 * the format; the reference's `mimeType` is its type.
 */
export const fetchCATReferenceBytes = async (
  downloadUrl: string,
  fetcher: typeof fetch = globalThis.fetch
) => {
  const controller = new AbortController()
  const timeout = setTimeout(
    () => controller.abort(),
    REFERENCE_BYTES_FETCH_TIMEOUT_MS
  )
  try {
    let response: Response
    try {
      response = await fetcher(downloadUrl, {
        credentials: "omit",
        signal: controller.signal,
      })
    } catch {
      throw new ReferenceDownloadError("Failed to fetch the reference file")
    }
    if (!response.ok) {
      throw new ReferenceDownloadError(
        `Failed to fetch the reference file: HTTP ${response.status}`
      )
    }
    return await response.arrayBuffer()
  } finally {
    clearTimeout(timeout)
  }
}

interface ReferenceErrorBody {
  code?: unknown
  params?: unknown
}

const errorResponse = (error: unknown) => {
  if (typeof error !== "object" || error === null || !("response" in error))
    return undefined
  return (
    error as { response?: { status?: number; data?: ReferenceErrorBody } }
  ).response
}

export const referenceErrorStatus = (error: unknown) =>
  errorResponse(error)?.status

/** The coded refusal's `code`, as sent. */
export const referenceErrorCode = (error: unknown) => {
  const code = errorResponse(error)?.data?.code
  return typeof code === "string" ? code : undefined
}

/**
 * The coded refusal's `params`. Error bodies skip the response interceptor, so their keys stay
 * snake_case as the server wrote them.
 */
export const referenceErrorParams = (
  error: unknown
): Record<string, unknown> | undefined => {
  const params = errorResponse(error)?.data?.params
  return typeof params === "object" && params !== null && !Array.isArray(params)
    ? (params as Record<string, unknown>)
    : undefined
}

const TEXT_UNAVAILABLE_REASONS: readonly ReferenceTextUnavailableReason[] = [
  "scanned",
  "encrypted",
  "parse_failed",
  "too_large",
]

export const parseReferenceTextUnavailableReason = (
  reason: unknown
): ReferenceTextUnavailableReason | null =>
  TEXT_UNAVAILABLE_REASONS.find((known) => known === reason) ?? null

/**
 * `409 text_unavailable` from `/text`: the reference has no text, and `params.reason` says why
 * (`null` while it has not been read, or for a reason this client does not know). `undefined` for
 * any other failure.
 */
export const referenceTextUnavailable = (
  error: unknown
): { reason: ReferenceTextUnavailableReason | null } | undefined => {
  if (
    referenceErrorStatus(error) !== 409 ||
    referenceErrorCode(error) !== "text_unavailable"
  )
    return undefined
  return {
    reason: parseReferenceTextUnavailableReason(
      referenceErrorParams(error)?.reason
    ),
  }
}

/**
 * `408`: one of the search's statements ran over the server's 10 seconds, typically a low
 * `min_score` over a large job. The limit is per statement (`statement_timeout`), not on the
 * search as a whole, which runs up to four of them and so can take longer than 10 seconds in total.
 */
export const isReferenceSearchTimeout = (error: unknown) =>
  referenceErrorStatus(error) === 408

/**
 * `400 invalid_reference_search` on the query: `"empty"` when the server found it blank once
 * trimmed, `"too_long"` when it ran over `params.limit_chars`. `undefined` for any other failure,
 * a refusal of another field included.
 */
export const referenceSearchQueryRefusal = (
  error: unknown
): "empty" | "too_long" | undefined => {
  if (
    referenceErrorStatus(error) !== 400 ||
    referenceErrorCode(error) !== "invalid_reference_search"
  )
    return undefined
  const params = referenceErrorParams(error)
  if (params?.field !== "query") return undefined
  return params.limit_chars === undefined ? "empty" : "too_long"
}
